import { useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import type { ChartAnnotation } from './types'

/** Extra x-axis height reserved for the annotation flag row below the labels. */
export const ANNOTATION_AXIS_HEIGHT = 74

/** An annotation resolved to the timestamps the x-axis is keyed on. */
export interface ChartAnnotationPoint {
  id: string
  ariaLabel: string
  content: ReactNode
  /** The annotation's anchor date as an epoch timestamp. */
  ts: number
  /** Draw the "no data before" hatch across the region before `ts` while open. */
  showNoDataOverlay: boolean
  /** Faint guide lines a flag reveals while active (one flag can stand in for
   * several grouped dates). */
  lineTimestamps: number[]
}

export interface UseChartAnnotationsResult {
  annotationPoints: ChartAnnotationPoint[]
  activeAnnotationId: string | null
  setActiveAnnotationId: React.Dispatch<React.SetStateAction<string | null>>
  activeAnnotation: ChartAnnotationPoint | null
  showAnnotations: boolean
  /** Close any open flag popover once the pointer is over plotted data, handing
   * control back to the tooltip. Pass to the chart's `onMouseMove`. */
  closeAnnotationOnGraphHover: (state: { isTooltipActive?: boolean }) => void
}

const toTimestamp = (date: string | Date): number =>
  typeof date === 'string' ? new Date(date).getTime() : date.getTime()

/**
 * Owns the shared timeline-annotation state for the time-axis charts
 * (TimeSeriesChart, ComposedChart): resolves each {@link ChartAnnotation} to the
 * x-axis timestamps, tracks which flag's popover is open, and hands control back
 * to the tooltip when the pointer returns to the plotted data. The open state
 * lives here (not in the Popover) so it survives Recharts re-rendering the flag
 * label and so the chart can close it from a graph-hover.
 */
export function useChartAnnotations(
  annotations: ChartAnnotation[],
): UseChartAnnotationsResult {
  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(
    null,
  )

  const closeAnnotationOnGraphHover = useCallback(
    (state: { isTooltipActive?: boolean }): void => {
      if (state?.isTooltipActive) {
        setActiveAnnotationId((current) => (current == null ? current : null))
      }
    },
    [],
  )

  const annotationPoints = useMemo<ChartAnnotationPoint[]>(
    () =>
      annotations.map((annotation) => ({
        id: annotation.id,
        ariaLabel: annotation.ariaLabel,
        content: annotation.content,
        ts: toTimestamp(annotation.date),
        showNoDataOverlay: annotation.showNoDataOverlay ?? false,
        lineTimestamps: (annotation.lineDates ?? [annotation.date]).map(
          toTimestamp,
        ),
      })),
    [annotations],
  )

  const activeAnnotation =
    annotationPoints.find((point) => point.id === activeAnnotationId) ?? null

  return {
    annotationPoints,
    activeAnnotationId,
    setActiveAnnotationId,
    activeAnnotation,
    showAnnotations: annotationPoints.length > 0,
    closeAnnotationOnGraphHover,
  }
}
